import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    FiArrowRight, FiArrowUpRight, FiBarChart2, FiBell, FiBookOpen,
    FiCheck, FiClipboard, FiFileText, FiLayers, FiMessageCircle, FiUsers,
} from 'react-icons/fi';
import { setIsAdmin } from '../reduxToolkit/reduxBoard';
import Footer from '../Footer';
import './HomeApp.scss';

const INQUIRY_URL = 'https://open.kakao.com/o/gk26Vv1h';
const learningSteps = [
    { icon: FiBookOpen, title: '수업 준비', text: '교재와 문제로 필요한 수업을 구성해요.', label: '교재 · 문제' },
    { icon: FiClipboard, title: '학습 진행', text: '수업과 과제를 학생에게 연결해요.', label: '수업 · 과제' },
    { icon: FiBarChart2, title: '결과 확인', text: '학습 결과를 다음 수업에 반영해요.', label: '평가 · 피드백' },
];
const features = [
    { icon: FiLayers, title: '필요한 교재와 문제를 한곳에', text: '교재와 문제를 살펴보고 수업에 맞는 학습 자료를 준비하세요.', tag: '수업 준비' },
    { icon: FiClipboard, title: '수업부터 과제까지 간편하게', text: '디지털 수업과 과제를 연결하고 학생들의 학습을 관리하세요.', tag: '학습 관리' },
    { icon: FiBarChart2, title: '학습 과정을 확인하는 리포트', text: '평가와 학습 결과를 확인하고 학생에게 필요한 피드백을 전하세요.', tag: '결과 확인' },
    { icon: FiUsers, title: '학생과 이어지는 소통', text: '학생 정보를 관리하고 필요한 수업 안내를 전달하세요.', tag: '학생 관리' },
];

function BrochureLink({ onOpen, className, children }) {
    if (onOpen) {
        return <button type="button" className={className} onClick={onOpen}>{children}</button>;
    }
    return <a href="/bruchure.pdf" className={className} target="_blank" rel="noopener noreferrer">{children}</a>;
}

export default function HomeApp({ onOpenBrochure }) {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector(state => state.reduxStore.isLoggedIn);
    const userInfo = useSelector(state => state.reduxStore.userInfo);
    const memberKind = String(userInfo?.kind ?? '');
    const workspace = memberKind === '2' ? '/LmsTec' : memberKind === '1' ? '/Lms' : '/storeBook';
    const primaryLabel = !isLoggedIn ? '로그인하고 시작하기' : memberKind === '2' ? '내 수업 관리하기' : memberKind === '1' ? '내 학습 시작하기' : '교재 둘러보기';

    useEffect(() => {
        dispatch(setIsAdmin(false));
    }, [dispatch]);

    return (
        <main className="lms-home" aria-labelledby="lms-home-title" tabIndex={0}>
            <div className="lms-home-shell">
                <section className="lms-home-hero">
                    <div className="lms-home-intro">
                        <p className="lms-home-eyebrow"><span aria-hidden="true" /> AI K-EDU · 통합 학습 관리</p>
                        <h1 id="lms-home-title">수업의 준비부터,<br /><em>성장의 기록까지.</em></h1>
                        <p className="lms-home-lead">교재와 문제, 과제와 평가를 한곳에서.<br />선생님이 수업에 집중할 수 있도록 돕습니다.</p>
                        <div className="lms-home-actions">
                            <Link to={isLoggedIn ? workspace : '/login'} className="lms-home-button lms-home-button-primary">{primaryLabel}<FiArrowRight aria-hidden="true" /></Link>
                            <BrochureLink onOpen={onOpenBrochure} className="lms-home-button lms-home-button-secondary"><FiFileText aria-hidden="true" />브로셔 보기</BrochureLink>
                        </div>
                        <p className="lms-home-intro-note"><FiCheck aria-hidden="true" /> 선생님과 학생을 연결하는 학습 공간</p>
                    </div>
                    <div className="lms-home-flow" aria-label="수업 준비부터 결과 확인까지 이어지는 학습 과정">
                        <div className="lms-home-flow-heading"><span>하나로 이어지는 학습</span><span className="lms-home-flow-badge">ALL IN ONE</span></div>
                        <ol className="lms-home-steps">
                            {learningSteps.map(({ icon: Icon, title, text, label }, index) => (
                                <li key={title} className="lms-home-step">
                                    <span className="lms-home-step-icon"><Icon aria-hidden="true" /></span>
                                    <div className="lms-home-step-copy"><span className="lms-home-step-label">0{index + 1} · {label}</span><h2>{title}</h2><p>{text}</p></div>
                                    <FiCheck className="lms-home-step-check" aria-hidden="true" />
                                </li>
                            ))}
                        </ol>
                        <p className="lms-home-flow-note">매일의 수업이 차곡차곡, 성장의 기록으로.</p>
                    </div>
                </section>
                <nav className="lms-home-shortcuts" aria-label="자주 찾는 서비스">
                    <Link to="/storeBook"><FiBookOpen className="lms-home-shortcut-icon" aria-hidden="true" /><span>교재·문제 구매</span><FiArrowUpRight aria-hidden="true" /></Link>
                    <Link to="/board"><FiBell className="lms-home-shortcut-icon" aria-hidden="true" /><span>공지사항</span><FiArrowUpRight aria-hidden="true" /></Link>
                    <BrochureLink onOpen={onOpenBrochure}><FiFileText className="lms-home-shortcut-icon" aria-hidden="true" /><span>서비스 브로셔</span><FiArrowUpRight aria-hidden="true" /></BrochureLink>
                    <a href={INQUIRY_URL} target="_blank" rel="noopener noreferrer" aria-label="카카오톡 이용 문의, 새 창"><FiMessageCircle className="lms-home-shortcut-icon" aria-hidden="true" /><span>이용 문의</span><FiArrowUpRight aria-hidden="true" /></a>
                </nav>
                <section className="lms-home-services" aria-labelledby="lms-home-services-title">
                    <div className="lms-home-section-heading">
                        <div><p className="lms-home-eyebrow">수업에 필요한 것만, 간결하게</p><h2 id="lms-home-services-title">관리는 가볍게.<br className="lms-home-mobile-break" /> 수업은 더 세심하게.</h2></div>
                        <p>준비부터 피드백까지,<br />흩어져 있던 학습 관리를 연결합니다.</p>
                    </div>
                    <div className="lms-home-feature-grid">
                        {features.map(({ icon: Icon, title, text, tag }) => (
                            <article className="lms-home-feature" key={title}>
                                <div className="lms-home-feature-top"><span className="lms-home-feature-icon"><Icon aria-hidden="true" /></span><span>{tag}</span></div>
                                <h3>{title}</h3><p>{text}</p>
                            </article>
                        ))}
                    </div>
                </section>
                <section className="lms-home-contact" aria-labelledby="lms-home-contact-title">
                    <div><p>처음 이용하시나요?</p><h2 id="lms-home-contact-title">우리 수업에 맞는 시작,<br />함께 찾아드릴게요.</h2><span>서비스 이용 방법이 궁금하다면 편하게 문의해 주세요.</span></div>
                    <a href={INQUIRY_URL} target="_blank" rel="noopener noreferrer" className="lms-home-button lms-home-button-contact" aria-label="카카오톡으로 문의하기, 새 창"><FiMessageCircle aria-hidden="true" />카카오톡으로 문의하기<FiArrowUpRight aria-hidden="true" /></a>
                </section>
                <div className="lms-home-footer">
                    <div className="lms-home-footer-top"><span>AI K-EDU</span><nav aria-label="이용 정책"><a href="/terms-of-service.txt" target="_blank" rel="noopener noreferrer">이용약관</a><a href="/privacy-policy.txt" target="_blank" rel="noopener noreferrer">개인정보처리방침</a></nav></div>
                    <Footer />
                </div>
            </div>
        </main>
    );
}
